<template>
  <div>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="mb-1 d-flex align-items-center justify-content-start mb-md-0"
        >
          <label>Show</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="mr-1 per-page-selector d-inline-block ml-50"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          class="mb-1 d-flex align-items-center justify-content-end mb-md-0"
        >
          <label class="mr-1">
            Search
          </label>
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              class="mr-1 d-inline-block"
              placeholder="Search..."
              v-model="searchWord"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <!-- Larger screen than mobile -->
    <div class="d-none d-md-flex">
      <b-table
        class="border"
        no-local-sorting
        @sort-changed="sortingChanged"
        responsive
        striped
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :items="product"
        :fields="productFields"
      >
        <template #cell(action)="productItem">
          <div class="d-flex justify-content-between">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="warning"
              class="mr-1 btn-icon"
              :to="{ name: 'admin-compare-product-edit', params: { key: productItem.item.KODE } }"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
          </div>
        </template>
      </b-table>
    </div>
    <!-- Mobile screen -->
    <div class="d-flex d-md-none flex-column">
      <!-- Loading -->
      <div v-if="isBusy" class="text-center text-danger my-2">
        <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
        <strong class="text-secondary">Loading...</strong>
      </div>
      <!-- Data -->
      <b-row
        v-if="!isBusy"
        v-for="(item, index) in product"
        :key="item.id"
        class="mb-3 list-item"
      >
        <b-col cols="12" class="d-flex flex-wrap justify-content-end align-items-center mb-1" style="gap: 1rem;">
          <strong class="flex-grow-1 truncate">{{ item.PRODUCT }}</strong>
          <div>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="warning"
              class="mr-1 btn-icon"
              :to="{ name: 'admin-compare-product-edit', params: { key: item.KODE } }"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
          </div>
        </b-col>
        <b-col cols="12" class="d-flex flex-wrap">
          <div style="font-size: 0.75rem;">
            <p><strong>GROUP:</strong> {{ item.category ? item.category.name : '-' }}</p>
            <p><strong>DIVISION:</strong> {{ item.DIVISION || '-' }}</p>
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ pagination.from }} to {{ pagination.to }} of
            {{ pagination.total }} entries
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
            @input="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
    <b-modal hide-footer title="Participants" size="xl" id="participant-modal">
      <participant-pcc-table :pccId="selectedPCC" />
    </b-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import VSelect from 'vue-select'
import {
  BRow,
  BCol,
  BTable,
  BFormCheckbox,
  BButton,
  BFormInput,
  BPagination,
  BModal,
  VBModal,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ParticipantPccTable from '@/views/admin/pcc/ParticipantPCCTable'

export default {
  components: {
    BTable,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    VSelect,
    BFormInput,
    BPagination,
    BModal,
    BSpinner,
    ParticipantPccTable,
  },

  directives: {
    Ripple,
    VBModal,
  },

  data() {
    return {
      sortBy: 'start_datetime',
      sortDesc: true,
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      pagination: {
        from: 1,
        to: 10,
        total: 0,
      },
      product: [],
      productFields: [
        {
          key: 'PRODUCT',
          label: 'Product Name'
        },
        {
          key: 'category',
          formatter: (val, key, item) => {
            return val ? val.name : ''
          },
          label: 'Group'
        },
        {
          key: 'DIVISION',
          label: 'Division'
        },
        'action',
      ],
      selectedPCC: '',
      searchWord: '',
    }
  },

  watch: {
    perPage() {
      this.fetchPcc()
    },
    currentPage() {
      this.fetchPcc()
    },
    searchWord(newVal) {
      this.searchByTitle()
    },
  },

  mounted() {
    this.fetchPcc()
  },

  methods: {
    fetchPcc() {
      this.isBusy = true

      this.$http
        .get('/compare-product', {
          params: {
            page: this.currentPage,
            perPage: this.perPage,
            s: this.searchWord.toString().trim(),
            sort: `${this.sortDesc ? '-' : ''}${this.sortBy}`,
          },
        })
        .then(({ data }) => {
          this.currentPage = data.current_page
          this.pagination.total = data.total
          this.pagination.from = data.from
          this.pagination.to = data.to
          this.product = data.data
        })
        .finally(() => {
          this.isBusy = false
        })
    },

    searchByTitle: debounce(function() {
      this.fetchPcc()
    }, 200),

    onOpenChange(pccId, isOpen) {
      const openUrl = isOpen ? `/pcc/open/${pccId}` : `/pcc/close/${pccId}`
      this.$http
        .post(openUrl)
        .then(() => {
          this.$notif(
            'Success',
            `PCC has been successfully ${isOpen ? 'opened' : 'closed'}.`,
            'success'
          )
          this.fetchPcc()
        })
        .catch(err => {
          this.$notif('Error', `${err.message}`, 'error')
        })
    },

    changePage(page) {
      this.currentPage = page
    },

    sortingChanged({ sortBy, sortDesc }) {
      ;(this.sortBy = sortBy), (this.sortDesc = sortDesc)
      this.fetchPcc()
    },

    deletePCC(pccId) {
      return this.$http.delete(`pcc/product/${pccId}`)
    },

    showMsgBoxDelete(pccId) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this item.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.deletePCC(pccId)
              .then(() => {
                this.$notif(
                  'Success',
                  'PCC has been successfully deleted.',
                  'success'
                )
                this.fetchPcc()
              })
              .catch(err => {
                this.$notif('Error', `${err.message}`, 'error')
              })
          }
        })
    },

    showRating(pccId) {
      this.selectedPCC = pccId
      this.$bvModal.show('participant-modal')
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
